// src/hooks/useAntiCheat.js
import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that implements anti-cheating mechanisms for quizzes and assessments
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.maxViolations - Maximum allowed violations before auto-submission
 * @param {Function} options.onMaxViolationsReached - Callback function when max violations reached
 * @param {Function} options.onViolation - Callback function when violation detected
 * @param {boolean} options.detectRightClick - Whether to detect right-click attempts
 * @param {boolean} options.detectCopy - Whether to detect copy attempts
 * @param {boolean} options.detectPaste - Whether to detect paste attempts
 * @param {boolean} options.detectPrint - Whether to detect print attempts
 * @param {boolean} options.detectDevTools - Whether to detect devtools opening
 * @param {boolean} options.detectTabSwitch - Whether to detect tab switching
 * @param {boolean} options.detectScreenCapture - Whether to detect screen capture attempts
 * @returns {Object} - Hook state and methods
 */
const useAntiCheat = ({
    maxViolations = 3,
    onMaxViolationsReached = () => { },
    onViolation = () => { },
    detectRightClick = true,
    detectCopy = true,
    detectPaste = true,
    detectPrint = true,
    detectDevTools = true,
    detectTabSwitch = true,
    detectScreenCapture = true
}) => {
    const [violations, setViolations] = useState(0);
    const devToolsTimeout = useRef(null);
    const lastFocusTime = useRef(Date.now());
    const warningsLogged = useRef({});
    const maxViolationsTriggered = useRef(false); // Prevent multiple auto-submit triggers

    // Function to log violations with rate limiting
    const logViolation = (type, details = {}) => {
        // Reduced rate limiting - allow violations to be counted more frequently
        const rateLimit = type === 'tab_switch' ? 2000 : 3000; // 2 seconds for tab switch, 3 seconds for others

        const now = Date.now();
        const lastTime = warningsLogged.current[type] || 0;

        if (now - lastTime < rateLimit) {
            return; // Skip this violation if the same type was logged too recently
        }

        warningsLogged.current[type] = now;

        setViolations(prev => {
            const newCount = prev + 1;

            console.log(`Violation detected: ${type}, Count: ${newCount}/${maxViolations}`);

            // Call the onViolation callback with violation details
            onViolation(type, details);

            // Check if we've reached max violations (only trigger once)
            if (newCount >= maxViolations && !maxViolationsTriggered.current) {
                maxViolationsTriggered.current = true;
                console.log('MAX VIOLATIONS REACHED - Triggering auto-submit (FIRST TIME ONLY)');
                onMaxViolationsReached();
            }

            return newCount;
        });
    };

    // Reset violations count
    const resetViolations = () => {
        setViolations(0);
        warningsLogged.current = {};
        maxViolationsTriggered.current = false; // Reset the trigger flag
    };

    // Right click detection
    useEffect(() => {
        if (!detectRightClick) return;

        const handleContextMenu = (e) => {
            e.preventDefault();
            logViolation('right_click', {
                message: 'Right-click function is disabled during assessment.',
                code: 'RIGHT_CLICK_DETECTED'
            });
            return false;
        };

        document.addEventListener('contextmenu', handleContextMenu);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [detectRightClick]);

    // Copy detection
    useEffect(() => {
        if (!detectCopy) return;

        const handleCopy = (e) => {
            e.preventDefault();
            logViolation('copy', {
                message: 'Content copying is not permitted during assessment.',
                code: 'COPY_ATTEMPT_DETECTED'
            });
            return false;
        };

        document.addEventListener('copy', handleCopy);

        return () => {
            document.removeEventListener('copy', handleCopy);
        };
    }, [detectCopy]);

    // Paste detection
    useEffect(() => {
        if (!detectPaste) return;

        const handlePaste = (e) => {
            e.preventDefault();
            logViolation('paste', {
                message: 'Pasting content is not permitted during assessment.',
                code: 'PASTE_ATTEMPT_DETECTED'
            });
            return false;
        };

        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [detectPaste]);

    // Print detection
    useEffect(() => {
        if (!detectPrint) return;

        const handleBeforePrint = () => {
            logViolation('print', {
                message: 'Printing is not permitted during assessment.',
                code: 'PRINT_ATTEMPT_DETECTED'
            });
        };

        window.addEventListener('beforeprint', handleBeforePrint);

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
        };
    }, [detectPrint]);

    // DevTools detection using resize method
    useEffect(() => {
        if (!detectDevTools) return;

        const threshold = 160;
        const originalWidth = window.outerWidth - window.innerWidth;
        const originalHeight = window.outerHeight - window.innerHeight;

        const checkDevTools = () => {
            const widthThreshold = window.outerWidth - window.innerWidth > originalWidth + threshold;
            const heightThreshold = window.outerHeight - window.innerHeight > originalHeight + threshold;

            if (widthThreshold || heightThreshold) {
                logViolation('dev_tools', {
                    message: 'Developer tools are not permitted during assessment.',
                    code: 'DEV_TOOLS_DETECTED'
                });
            }

            devToolsTimeout.current = setTimeout(checkDevTools, 1000);
        };

        devToolsTimeout.current = setTimeout(checkDevTools, 1000);

        return () => {
            clearTimeout(devToolsTimeout.current);
        };
    }, [detectDevTools]);

    // Tab switch detection
    useEffect(() => {
        if (!detectTabSwitch) return;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                logViolation('tab_switch', {
                    message: 'Leaving the assessment window is not permitted during the exam.',
                    code: 'TAB_SWITCH_DETECTED'
                });
            }
        };

        const handleFocus = () => {
            const now = Date.now();
            const timeDiff = now - lastFocusTime.current;

            // If the window was unfocused for more than 1 second, log it as a tab switch
            if (timeDiff > 1000) {
                logViolation('tab_switch', {
                    message: 'Leaving the assessment window is not permitted during the exam.',
                    code: 'WINDOW_FOCUS_LOST'
                });
            }

            lastFocusTime.current = now;
        };

        const handleBlur = () => {
            lastFocusTime.current = Date.now();
            logViolation('tab_switch', {
                message: 'Assessment window lost focus. Please remain in the exam window.',
                code: 'WINDOW_BLUR_DETECTED'
            });
        };

        // Additional event listeners for better detection
        const handlePageHide = () => {
            logViolation('tab_switch', {
                message: 'Page navigation detected during assessment.',
                code: 'PAGE_HIDE_DETECTED'
            });
        };

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
            logViolation('tab_switch', {
                message: 'Attempted to leave assessment page.',
                code: 'PAGE_UNLOAD_ATTEMPT'
            });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [detectTabSwitch]);

    // Screen capture detection (for browsers that support the Screen Capture API)
    useEffect(() => {
        if (!detectScreenCapture || typeof navigator.mediaDevices === 'undefined') return;

        const checkScreenCapture = async () => {
            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });

                // If we get here, a screen capture has been initiated
                stream.getTracks().forEach(track => track.stop());

                logViolation('screen_capture', {
                    message: 'Screen recording or capture is not permitted during assessment.',
                    code: 'SCREEN_CAPTURE_DETECTED'
                });
            } catch (err) {
                // User denied screen capture permission, which is expected behavior
            }
        };

        // We cannot automatically detect screen capture initiation,
        // but we can periodically check permission status on browsers that support it
        const captureInterval = setInterval(() => {
            if (document.pictureInPictureElement) {
                logViolation('screen_capture', {
                    message: 'Picture-in-picture mode is not permitted during assessment.',
                    code: 'PIP_MODE_DETECTED'
                });
            }
        }, 5000);

        return () => {
            clearInterval(captureInterval);
        };
    }, [detectScreenCapture]);

    // Additional keyboard shortcut detection
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Detect common screenshot shortcuts
            if (
                (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
                (e.ctrlKey && e.key === 'u') ||
                (e.key === 'F12') ||
                (e.ctrlKey && e.key === 's')
            ) {
                e.preventDefault();
                logViolation('keyboard_shortcut', {
                    message: 'This keyboard shortcut is disabled during assessment.',
                    code: 'RESTRICTED_SHORTCUT_DETECTED'
                });
                return false;
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Prevent drag and drop
    useEffect(() => {
        const handleDragStart = (e) => {
            e.preventDefault();
            return false;
        };

        document.addEventListener('dragstart', handleDragStart);

        return () => {
            document.removeEventListener('dragstart', handleDragStart);
        };
    }, []);

    return {
        violations,
        resetViolations
    };
};

export default useAntiCheat;